import { useCallback, useEffect, useRef } from 'react';

/**
 * ProviderResultCardWrapper Component
 *
 * This component wraps a provider result card and handles specific behaviors such as:
 * - Updating the URL with the `activeProvider` parameter when a card is clicked.
 * - Scrolling to the active provider card when the `activeProvider` parameter matches the card's ID.
 * - Managing focus for accessibility by focusing the first anchor tag or the card itself.
 *
 * Props:
 * - `children` (ReactNode): The content to be rendered inside the card.
 * - `id` (string): The unique identifier for the card, used to match the `activeProvider` parameter in the URL.
 * - `...props` (object): Additional props passed to the `<li>` element.
 *
 * Example Usage:
 * ```jsx
 * <ProviderResultCardWrapper id="provider-123">
 *   <ProviderCard />
 * </ProviderResultCardWrapper>
 * ```
 */

export const ProviderResultCardWrapper = ({ children, id, ...props }) => {
    const elRef = useRef(null);
    const hasRestoredRef = useRef(false);

    /**
     * Handle card click to update the URL with the activeProvider parameter
     * only if the click is on an anchor tag within the card.
     */
    const onCardClick = useCallback(
        (event) => {
            const isAnchorTag = event.target.tagName === 'A' || event.target.closest('a');

            if (!isAnchorTag) {
                return;
            }

            const url = new URL(window.location);
            url.searchParams.set('activeProvider', id);

            window.history.replaceState({}, '', url);
        },
        [id],
    );

    const centerActiveCard = useCallback(() => {
        elRef.current?.scrollIntoView({ behavior: 'instant', block: 'center' });
    }, [elRef]);

    const focusActiveCard = useCallback(() => {
        const el = elRef.current;

        // find the first anchor tag in the card and focus it
        // if no anchor tag, focus the card itself.
        // preventScroll: centering is owned by centerActiveCard — focus must not move the viewport.
        const anchor = el?.querySelector('a');

        if (anchor) {
            anchor?.focus({ preventScroll: true });
        } else {
            el?.setAttribute('tabindex', '0');
            el?.focus({ preventScroll: true });
        }
    }, [elRef]);

    /**
     * When the component renders to the page the first time, scroll to the active provider card
     * if the activeProvider param matches the card's ID.
     */
    useEffect(() => {
        if (hasRestoredRef.current) {
            return undefined;
        }

        // Live URL read — react-router's useLocation does not reflect replaceState.
        const activeCardId = new URLSearchParams(window.location.search).get('activeProvider');

        if (activeCardId !== id) {
            return undefined;
        }

        // Check immediately on mount — card may already be fully rendered with no further mutations.
        hasRestoredRef.current = true;
        centerActiveCard();
        focusActiveCard();

        // The restored position gets knocked off after this first scroll by content above the
        // card (images, availability calendars) finishing layout and pushing the card down. On
        // slower mobile connections that reflow lands well after a short fixed window, so the
        // card ends up off-screen and the user cannot find the provider they came back to.
        // Keep the card anchored across the whole load: re-center the instant the list reflows
        // (ResizeObserver) or on any residual drift (poll), bailing the moment the user actually
        // scrolls so we never fight their input. AHSMKTG-4049
        if (typeof window === 'undefined') {
            return undefined;
        }

        let settled = false;
        let poll;
        let hardCap;
        let graceTimer;
        let resizeObserver;
        // Ignore the navigation gesture's residual touch/scroll for a beat so a mobile
        // back-swipe does not cancel the guard before the card has settled.
        let inGrace = true;

        const stop = () => {
            if (settled) {
                return;
            }
            settled = true;
            clearInterval(poll);
            clearTimeout(hardCap);
            clearTimeout(graceTimer);
            resizeObserver?.disconnect();
            window.removeEventListener('wheel', onUserScroll);
            window.removeEventListener('touchmove', onUserScroll);
            window.removeEventListener('keydown', onUserKey);
        };

        const onUserScroll = () => {
            if (!inGrace) {
                stop();
            }
        };

        // Keys that scroll the page count as user intent to leave the restored position.
        const scrollKeys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '];
        const onUserKey = (event) => {
            if (!inGrace && scrollKeys.includes(event.key)) {
                stop();
            }
        };

        // Re-center whenever the active card drifts more than a pixel or two from center.
        const DRIFT_TOLERANCE_PX = 2;
        const recenterIfDrifted = () => {
            const el = elRef.current;
            if (!el) {
                return;
            }
            const rect = el.getBoundingClientRect();
            const offsetFromCenter = rect.top + rect.height / 2 - window.innerHeight / 2;
            if (Math.abs(offsetFromCenter) > DRIFT_TOLERANCE_PX) {
                centerActiveCard();
            }
        };

        // ResizeObserver fires on the frame the list grows (an image or availability calendar
        // above the card finishing layout), correcting drift immediately instead of waiting for
        // the next poll tick. The poll is a fallback for reflows the observer misses.
        const listEl = elRef.current?.closest('ul') ?? elRef.current?.parentElement;
        if (listEl && typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(recenterIfDrifted);
            resizeObserver.observe(listEl);
        }
        poll = setInterval(recenterIfDrifted, 100);

        window.addEventListener('wheel', onUserScroll, { passive: true });
        window.addEventListener('touchmove', onUserScroll, { passive: true });
        window.addEventListener('keydown', onUserKey);

        graceTimer = setTimeout(() => {
            inGrace = false;
        }, 250);
        // Upper bound for the slowest reflow on a mobile connection; the guard stops early the
        // moment the user scrolls, so this only matters when the page is left untouched.
        hardCap = setTimeout(stop, 4000);

        return stop;
    }, [id, centerActiveCard, focusActiveCard]);

    return (
        <li {...props} ref={elRef} id={id} onClick={onCardClick}>
            {children}
        </li>
    );
};
